// PassengersPage — Passenger details step of the booking funnel.
// Dark palette. Per-passenger form rows with contact + frequent flyer sections.
// TODO(booking-API-PR): wire to POST /bookings (passenger details body).

import { useNavigate } from 'react-router-dom';
import { BookingProgressBar } from '@/components/BookingProgressBar';
import { Button, Input } from '@/components/primitives';
import { BookingProvider, useBooking } from '@/lib/booking-store';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  PASSENGER_TITLES,
  NATIONALITIES,
  FF_PROGRAMS,
  MOCK_FLIGHT,
  PASSENGER_DEFAULT,
  formatPaise,
  AIRPORT_TAXES_PAISE,
  type Passenger,
} from '@/lib/booking-mock';

// ─── SELECT FIELD ─────────────────────────────────────────────────────────────
function BSelect({
  label,
  value,
  onChange,
  options,
}: {
  label:    string;
  value:    string;
  onChange: (v: string) => void;
  options:  ReadonlyArray<string | { v: string; l: string }>;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label
        className="text-[10px] font-bold tracking-[1.5px] uppercase text-[#8C98B0]"
        style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
      >
        {label}
      </label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full h-12 rounded-xl px-4 bg-[#0A0F1C] text-[#F4F3F0] border border-white/[0.14] text-sm font-medium focus:outline-none focus:border-[#FF5A3C] transition-colors cursor-pointer"
        style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif', colorScheme: 'dark' }}
      >
        {options.map(o => {
          const v = typeof o === 'string' ? o : o.v;
          const l = typeof o === 'string' ? o : o.l;
          return <option key={v} value={v}>{l}</option>;
        })}
      </select>
    </div>
  );
}

// ─── INNER ────────────────────────────────────────────────────────────────────
function PassengersInner() {
  const navigate            = useNavigate();
  const isMobile            = useIsMobile();
  const { state, dispatch } = useBooking();
  const p                   = state.passengers[0] ?? { ...PASSENGER_DEFAULT };
  const f                   = MOCK_FLIGHT;

  const totalPaise = state.selectedFare.pricePaise + AIRPORT_TAXES_PAISE;

  function patchPassenger(patch: Partial<Passenger>) {
    dispatch({ type: 'SET_PASSENGER', index: 0, patch });
  }

  function patchContact(field: 'email' | 'phone', val: string) {
    if (field === 'email') dispatch({ type: 'SET_CONTACT', email: val, phone: state.contactPhone });
    else dispatch({ type: 'SET_CONTACT', email: state.contactEmail, phone: val });
  }

  function handleContinue() {
    navigate('/seats');
  }

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <BookingProgressBar step="passengers" />

      <div style={{ padding: '32px clamp(16px, 4vw, 64px) 80px', maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 340px', gap: 28 }}>

          {/* ── Left: forms ──────────────────────────────────────────────── */}
          <div>
            <h2
              className="text-[28px] font-semibold text-[#F4F3F0] mb-1.5 tracking-tight"
              style={{ fontFamily: '"Fraunces Variable","Fraunces",Georgia,serif', letterSpacing: '-0.02em' }}
            >
              Passenger details
            </h2>
            <p
              className="text-[14px] text-[#8C98B0] mb-7"
              style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
            >
              Enter details exactly as they appear on your travel document.
            </p>

            {/* Passenger 1 card */}
            <div className="bg-[#131C30] rounded-[20px] border border-white/[0.07] p-7 mb-5">
              {/* Card header */}
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-8 h-8 rounded-[10px] bg-[#FF5A3C] flex items-center justify-center text-white text-[13px] font-bold">
                  1
                </div>
                <div
                  className="text-[16px] font-bold text-[#F4F3F0]"
                  style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                >
                  Adult passenger
                </div>
                <span className="ml-auto px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-[0.5px] text-[#FF5A3C] bg-[rgba(255,90,60,0.12)]">
                  ADULT
                </span>
              </div>

              {/* Title + name row */}
              <div className="flex gap-3 mb-3">
                <div className="w-24 flex-shrink-0">
                  <BSelect
                    label="Title"
                    value={p.title}
                    onChange={v => patchPassenger({ title: v })}
                    options={[...PASSENGER_TITLES]}
                  />
                </div>
                <div className="flex-1">
                  <Input
                    label="First name"
                    value={p.firstName}
                    onChange={e => patchPassenger({ firstName: e.target.value })}
                    placeholder="Alex"
                  />
                </div>
                <div className="flex-1">
                  <Input
                    label="Last name"
                    value={p.lastName}
                    onChange={e => patchPassenger({ lastName: e.target.value })}
                    placeholder="Chen"
                  />
                </div>
              </div>

              {/* DOB + nationality */}
              <div className="flex gap-3 mb-3">
                <div className="flex-1">
                  <Input
                    label="Date of birth"
                    type="date"
                    value={p.dob}
                    onChange={e => patchPassenger({ dob: e.target.value })}
                  />
                </div>
                <div className="flex-1">
                  <BSelect
                    label="Nationality"
                    value={p.nationality}
                    onChange={v => patchPassenger({ nationality: v })}
                    options={[...NATIONALITIES]}
                  />
                </div>
              </div>

              {/* Passport + expiry */}
              <div className="flex gap-3">
                <div className="flex-1">
                  <Input
                    label="Passport / National ID"
                    value={p.passport}
                    onChange={e => patchPassenger({ passport: e.target.value })}
                    placeholder="P1234567"
                  />
                </div>
                <div className="flex-1">
                  <Input
                    label="Expiry date"
                    type="date"
                    value={p.expiry}
                    onChange={e => patchPassenger({ expiry: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Contact details card */}
            <div className="bg-[#131C30] rounded-[20px] border border-white/[0.07] p-7 mb-5">
              <div
                className="text-[14px] font-bold text-[#F4F3F0] mb-5"
                style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
              >
                Contact details
              </div>
              <div className="flex gap-3 mb-2">
                <div className="flex-[1.6]">
                  <Input
                    label="Email address"
                    type="email"
                    value={state.contactEmail}
                    onChange={e => patchContact('email', e.target.value)}
                    placeholder="you@email.com"
                  />
                </div>
                <div className="flex-1">
                  <Input
                    label="Phone number"
                    value={state.contactPhone}
                    onChange={e => patchContact('phone', e.target.value)}
                    placeholder="+91 98765 43210"
                  />
                </div>
              </div>
              <p
                className="text-[12px] text-[#8C98B0]"
                style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
              >
                Boarding pass and booking confirmation will be sent to this email.
              </p>
            </div>

            {/* Frequent flyer card */}
            <div className="bg-[#131C30] rounded-[20px] border border-white/[0.07] p-7">
              <div
                className="text-[14px] font-bold text-[#F4F3F0] mb-1"
                style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
              >
                Frequent flyer
              </div>
              <p
                className="text-[12px] text-[#8C98B0] mb-4"
                style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
              >
                Add your miles number to earn points on this flight.
              </p>
              <div className="flex gap-3">
                <div className="flex-1">
                  <BSelect
                    label="Airline programme"
                    value={state.ffProgram}
                    onChange={v => dispatch({ type: 'SET_FF', program: v, number: state.ffNumber })}
                    options={[...FF_PROGRAMS]}
                  />
                </div>
                <div className="flex-1">
                  <Input
                    label="Membership number (optional)"
                    value={state.ffNumber}
                    onChange={e =>
                      dispatch({ type: 'SET_FF', program: state.ffProgram, number: e.target.value })
                    }
                    placeholder="KF123456789"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ── Right sidebar ─────────────────────────────────────────────── */}
          <div className="sticky top-[64px] self-start">
            <div className="bg-[#131C30] rounded-[24px] border border-white/[0.07] p-6">
              {/* Eyebrow */}
              <div
                className="text-[11px] font-semibold tracking-[1.5px] text-[#8C98B0] mb-4"
                style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
              >
                BOOKING SUMMARY
              </div>

              {/* Flight */}
              <div className="pb-4 border-b border-white/[0.07]">
                <div
                  className="text-[14px] font-bold text-[#F4F3F0]"
                  style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                >
                  {f.airline} · {state.selectedFare.label}
                </div>
                <div
                  className="text-[12px] text-[#8C98B0] mt-1"
                  style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                >
                  {f.from} → {f.to} · Mar 17 · {f.duration}
                </div>
              </div>

              {/* Passenger */}
              <div className="py-4 border-b border-white/[0.07]">
                <div
                  className="text-[14px] font-semibold text-[#F4F3F0] mb-2"
                  style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                >
                  1 passenger
                </div>
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-[10px] bg-[rgba(255,90,60,0.12)] flex items-center justify-center text-base">
                    👤
                  </div>
                  <div>
                    <div
                      className="text-[14px] font-semibold text-[#F4F3F0]"
                      style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                    >
                      {p.title} {p.firstName} {p.lastName}
                    </div>
                    <div
                      className="text-[11px] text-[#8C98B0]"
                      style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                    >
                      Adult · {p.nationality}
                    </div>
                  </div>
                </div>
              </div>

              {/* Total */}
              <div className="flex justify-between pt-4 mb-4">
                <span
                  className="text-[20px] font-bold text-[#F4F3F0]"
                  style={{ fontFamily: '"Fraunces Variable","Fraunces",Georgia,serif' }}
                >
                  Total
                </span>
                <span
                  className="text-[20px] font-bold text-[#F4F3F0]"
                  style={{ fontFamily: '"Fraunces Variable","Fraunces",Georgia,serif' }}
                >
                  {formatPaise(totalPaise)}
                </span>
              </div>

              <Button
                variant="flame"
                size="lg"
                full
                onClick={handleContinue}
                className="rounded-[14px] font-bold"
              >
                Continue to seats →
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── EXPORT ──────────────────────────────────────────────────────────────────
export function PassengersPage() {
  return (
    <BookingProvider>
      <PassengersInner />
    </BookingProvider>
  );
}
